import React from "react";
import shallow from "zustand/shallow";
import { useStore } from "./state";
import moment from "moment-timezone";
import "chartjs-adapter-moment";
import { flatMap, filter, concat } from "lodash";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  TimeScale,
  TimeSeriesScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
);

// ### UNIX-Timestamp	 Date/Time	 Discharge(Wh)	 Charge(Wh)	 Production(Wh)	 Consumption(Wh)	 Feed-in(Wh)	 From grid(Wh)

export default function LocationDetail(props) {
  const { active_location, location_data, chart_range, setChartRange } =
    useStore(
      (state) => ({
        active_location: state.active_location,
        location_data: state.location_data,
        chart_range: state.chart_range,
        setChartRange: state.setChartRange,
      }),
      shallow
    );

  const { innerWidth: width, innerHeight: height } = window;

  if (!active_location.data) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          alignItems: "center",
        }}
      >
        <h5>
          We're unable to load solar inverter data for this site at this time.
        </h5>
      </div>
    );
  }

  /*
    Green is “Clean renewable energy produced”
    Yellow is “Clean Energy Self Powering Real Time”
    turquoise is “Extra Clean Energy To Energy Storage”
    Orange is “ Clean Stored Energy Supplying Demand”
    Blue is “Grid Energy Supplying Real Time Demand”
    Pink is “Excess Clean Energy to Grid”
    Red is Grid Energy In or Extra Clean Energy Exported To Grid
     */

  let _location_data = active_location.data;
  let chart_data = [];
  chart_data = _location_data.data;
  //if(chart_range_week === 'week') {
  let week_before = moment(_location_data.max_date)
    .add(-1, "week")
    .set({ hours: 0, minutes: 0, seconds: 0 })
    .unix();
  let chart_data_week = filter(chart_data, (o) => o[0] > week_before);
  //}

  const labels = flatMap(chart_data, (d) => {
    return moment.unix(d[0]).add(9, "hours").format("MMM D LT");
  });
  const discharge = flatMap(chart_data, (d) => d[2]);
  const charge = flatMap(chart_data, (d) => d[3]);
  const production = flatMap(chart_data, (d) => d[4]);
  const consumption = flatMap(chart_data, (d) => d[5]);
  const feed_in = flatMap(chart_data, (d) => d[6]);
  const from_grid = flatMap(chart_data, (d) => d[7]);

  const labels_week = flatMap(chart_data_week, (d) => {
    return moment.unix(d[0]).add(9, "hours").format("MMM D LT");
  });
  const discharge_week = flatMap(chart_data_week, (d) => d[2]);
  const charge_week = flatMap(chart_data_week, (d) => d[3]);
  const production_week = flatMap(chart_data_week, (d) => d[4]);
  const consumption_week = flatMap(chart_data_week, (d) => d[5]);
  const feed_in_week = flatMap(chart_data_week, (d) => d[6]);
  const from_grid_week = flatMap(chart_data_week, (d) => d[7]);

  const datasets = [
    /*{
            label: 'Feed In',
            data: feed_in,
            //fill: true,
            borderWidth: 1,
            borderColor: 'rgb(216,99,255)',
            backgroundColor: 'rgba(216,99,255, 0.5)',
            yAxisID: 'y',
        },
        {
            label: 'Consumption',
            data: consumption,
            //fill: true,
            borderWidth: 1,
            borderColor: 'rgb(255,136,136)',
            backgroundColor: 'rgb(255,136,136)',
            yAxisID: 'y',
        },*/
    {
      label: "From Grid",
      data: from_grid,
      //fill: true,
      borderWidth: 1,
      borderColor: "rgb(255,0,0)",
      backgroundColor: "rgb(255,0,0)",
      yAxisID: "y",
    },
    {
      label: "Charge",
      data: charge,
      //fill: true,
      borderWidth: 1,
      borderColor: "rgb(99,213,255)",
      backgroundColor: "rgb(99,213,255)",
      yAxisID: "y",
    },
    {
      label: "Production",
      data: production,
      //fill: true,
      borderWidth: 1,
      borderColor: "rgb(65,252,0)",
      backgroundColor: "rgb(65,252,0)",
      yAxisID: "y",
    },
    {
      label: "Discharge",
      data: discharge,
      borderWidth: 1,
      //fill: true,
      borderColor: "rgba(255, 99, 132, 0.5)",
      backgroundColor: "rgba(255, 99, 132, 0.5)",
      yAxisID: "y",
    },
  ];
  const datasets_week = [
    /*{
            label: 'Feed In',
            data: feed_in,
            //fill: true,
            borderWidth: 1,
            borderColor: 'rgb(216,99,255)',
            backgroundColor: 'rgba(216,99,255, 0.5)',
            yAxisID: 'y',
        },
        {
            label: 'Consumption',
            data: consumption,
            //fill: true,
            borderWidth: 1,
            borderColor: 'rgb(255,136,136)',
            backgroundColor: 'rgb(255,136,136)',
            yAxisID: 'y',
        },*/
    {
      label: "From Grid",
      data: from_grid_week,
      //fill: true,
      borderWidth: 1,
      borderColor: "rgb(255,0,0)",
      backgroundColor: "rgb(255,0,0)",
      yAxisID: "y",
    },
    {
      label: "Charge",
      data: charge_week,
      //fill: true,
      borderWidth: 1,
      borderColor: "rgb(99,213,255)",
      backgroundColor: "rgb(99,213,255)",
      yAxisID: "y",
    },
    {
      label: "Production",
      data: production_week,
      //fill: true,
      borderWidth: 1,
      borderColor: "rgb(65,252,0)",
      backgroundColor: "rgb(65,252,0)",
      yAxisID: "y",
    },
    {
      label: "Discharge",
      data: discharge_week,
      borderWidth: 1,
      //fill: true,
      borderColor: "rgba(255, 99, 132, 0.5)",
      backgroundColor: "rgba(255, 99, 132, 0.5)",
      yAxisID: "y",
    },
  ];

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    stacked: true,
    plugins: {
      title: {
        display: false,
        text: "",
      },
      legend: {
        labels: {
          color: "white",
          font: {
            size: 8,
          },
          boxWidth: width * 0.05,
        },
      },
    },
    onHover: (event) => {
      if (!event || !event.chart || !event.chart.tooltip) return;
      let _dp = event.chart.tooltip.dataPoints;
      let _data = [];
      for (let i in _dp) {
        _data[i] = {
          label: _dp[i].dataset.label,
          value: Number(_dp[i].formattedValue),
        };
      }
      updateChart(_data);
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        ticks: {
          color: "white",
          font: {
            size: 10, //width > 960 ? 14 : 8
          },
          callback: function (label) {
            return label + " Wh";
          },
        },
      },
      x: {
        display: true,
        type: "time",
        position: "bottom",
        distribution: "linear",
        ticks: {
          source: "labels",
          color: "white",
          font: {
            size: 10, //width > 960 ? 14 : 8
          },
          callback: (label, index) => {
            let ts = moment(labels[index]);
            if (
              ts.format("LT") === "12:00 PM" ||
              ts.format("LT") === "12:00 AM"
            )
              return moment(labels[index]).format("MMM D");
          },
        },
      },
    },
  };

  function getDatesInRange(startDate, endDate) {
    const date = new Date(startDate.getTime());
    const dates = [];
    while (date <= endDate) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return dates;
  }

  const data = {
    labels: [],
    datasets: [],
  };
  data.datasets = datasets;
  data.labels = labels;

  const data_week = {
    labels: [],
    datasets: [],
  };
  data_week.datasets = datasets_week;
  data_week.labels = labels_week;

  let title = "Loading Energy Usage",
    title_week = "";
  if (!!_location_data && !!_location_data.min_date) {
    title_week =
      "Energy Data from " +
      moment.unix(week_before).format("M/D/Y") +
      " to " +
      _location_data.max_date;
    title =
      "Energy Data from " +
      _location_data.min_date +
      " to " +
      _location_data.max_date;
  }

  let buttonBgColor = "#f7f7f7";
  let activeButtonBgColor = "#ebebeb";

  const default_positions = [
    { top: 42.1, left: 32.7 },
    { top: 12.3, left: 35 },
    { top: 29.4, left: 34.9 },
    { top: 12.65, left: 51 },
    { top: 56.3, left: 32.8 },
    { top: 62.1, left: 20.2 },
    { top: 78.5, left: 78.4 },
    { top: 24.6, left: 42.5 },
    { top: 78.5, left: 78.4 },
  ];
  const box_positions = [
    { top: 44.1, left: 36.7 },
    { top: 9.3, left: 29 },
    { top: 29.4, left: 34.9 },
    { top: 14, left: 64 },
    { top: 57.4, left: 38.8 },
    { top: 81.1, left: 12.2 },
    { top: 85, left: 61.4 },
    { top: 31, left: 45.5 },
    { top: 91, left: 62.4 },
  ];
  function setPositions() {
    //const lines = document.getElementsByClassName("lines");
    //console.log(lines)
    //lines[0].style.height = wh + 'px';
    //lines[0].style.width = ww + 'px';
    const img = document.getElementsByClassName("bgImg");
    if (!!img[0]) {
      const imgWidth = img[0].width;
      const scale = imgWidth / 2000;
      for (let i = 0; i < 9; i++) {
        const collection = document.getElementsByClassName("path" + (i + 1));
        if (collection.length > 0) {
          for (let j = 0; j < collection.length; j++) {
            if (!collection[j].classList.contains("box")) {
              collection[j].style.scale = scale;
            }
            collection[j].style.top =
              (default_positions[i].top / 100) * imgWidth + "px";
            collection[j].style.left =
              (default_positions[i].left / 100) * imgWidth + "px";
          }
        }
        const boxes = document.getElementsByClassName("box" + (i + 1));
        if (boxes.length > 0) {
          for (let j = 0; j < boxes.length; j++) {
            if (!boxes[j].classList.contains("box")) {
              boxes[j].style.scale = scale;
            }
            boxes[j].style.top = (box_positions[i].top / 100) * imgWidth + "px";
            boxes[j].style.left =
              (box_positions[i].left / 100) * imgWidth + "px";
          }
        }
      }
    } else {
      setTimeout(() => {
        console.log("getting positions again");
        setPositions();
      }, 100);
    }
  }
  window.addEventListener("resize", setPositions);
  setPositions();

  function showLines(arr) {
    for (let k = 1; k <= 9; k++) {
      if (arr.includes(k)) {
        continue;
      }
      let col = document.getElementsByClassName("path" + k);
      for (let j = 0; j < col.length; j++) {
        col[j].style.display = "none";
      }
    }
    for (let i in arr) {
      let col = document.getElementsByClassName("path" + arr[i]);
      for (let j = 0; j < col.length; j++) {
        col[j].style.display = "block";
      }
    }
  }

  function setState(state) {
    switch (state) {
      default:
      case 1:
        showLines([6, 7]);
        break;
      case 2:
        showLines([1, 2, 3, 6, 7]);
        break;
      case 3:
        showLines([1, 2, 3, 8, 6, 7]);
        break;
      case 4:
        showLines([5]);
        break;
      case 5:
        showLines([1, 2, 3]);
        break;
      case 6:
        showLines([1, 2, 3, 8]);
        break;
      case 7:
        showLines([1, 2, 3, 4, 9]);
        break;
      case 8:
        showLines([]);
        break;
      case 9:
        showLines([1, 2, 3, 4, 5, 6, 7, 8, 9]);
        break;
    }
  }
  setState(9);

  /* system states:
    1. drawing from grid with no production or charging
    2. drawing from grid with production and not charging
    3. drawing from grid with production and charging
    4. not drawing from grid with discharge, no production and not charging
    5. not drawing from grid with production and not charging
    6. not drawing from grid with production and charging
    7. not drawing from grid with production and sending to grid
    8. system inert
 */
  const updateChart = (data) => {
    if (!data || data.length === 0) return;
    if (data[0].value > 1) {
      //drawing from grid
      if (data[2].value > 1) {
        // production
        if (data[1].value > 1) {
          //charging
          setState(3);
        } else {
          //not charging
          setState(2);
        }
      } else {
        //no production or charging
        setState(1);
      }
    } else {
      //not drawing from grid
      if (data[2].value > 1) {
        // production
        if (data[1].value > 1) {
          //charging
          setState(6);
        } else {
          //not charging
          setState(7);
        }
      } else {
        if (data[3].value > 1) {
          //discharging
          setState(4);
        } else {
          setState(8);
        }
      }
    }
  };
  /*
    Green is “Clean renewable energy produced”
    Yellow is “Clean Energy Self Powering Real Time”
    turquoise is “Extra Clean Energy To Energy Storage”
    Orange is “ Clean Stored Energy Supplying Demand”
    Blue is “Grid Energy Supplying Real Time Demand”
    Pink is “Excess Clean Energy to Grid”
    Red is Grid Energy In or Extra Clean Energy Exported To Grid
     */

  return (
    <div>
      <div
        className="detail-wrap"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-evenly",
        }}
      >
        <div
          className="graphs"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            width: "50%",
            alignSelf: "flex-start",
            paddingLeft: 40,
            paddingRight: 40,
          }}
        >
          <div style={{ position: "relative" }}>
            <strong>{title_week}</strong>
            <Line options={options} data={data_week} />
          </div>
          <div style={{ position: "relative" }}>
            <strong>{title}</strong>
            <Line options={options} data={data} />
          </div>
        </div>
        <div
          id="bg"
          style={{ maxHeight: width / 2, height: width / 2, width: width / 2 }}
        >
          <div className="lines">
            <svg>
              <filter id="glow">
                <fegaussianblur
                  className="blur"
                  result="coloredBlur"
                  stdDeviation="12"
                ></fegaussianblur>
                <femerge>
                  <femergenode in="coloredBlur"></femergenode>
                  <femergenode in="coloredBlur"></femergenode>
                  <femergenode in="coloredBlur"></femergenode>
                  <femergenode in="SourceGraphic"></femergenode>
                </femerge>
              </filter>
              <filter id="glow2">
                <fegaussianblur
                  className="blur"
                  result="coloredBlur"
                  stdDeviation="6"
                ></fegaussianblur>
                <femerge>
                  <femergenode in="coloredBlur"></femergenode>
                  <femergenode in="coloredBlur"></femergenode>
                  <femergenode in="coloredBlur"></femergenode>
                  <femergenode in="SourceGraphic"></femergenode>
                </femerge>
              </filter>
            </svg>

            <svg
              className="path1"
              width="166"
              height="92"
              viewBox="0 0 166 92"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M155.5 0.5V63.5L2 82"
                style={{ stroke: "#de953c" }}
              />
            </svg>
            <svg
              className="path1"
              width="166"
              height="92"
              viewBox="0 0 166 92"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash-rev"
                d="M155.5 0.5V63.5L2 82"
                style={{ stroke: "#f4ff98" }}
              />
            </svg>
            <div className="path1 box box1" style={{ borderColor: "#f4ff98" }}>
              Clean Energy Self
              <br />
              Powering Real Time
            </div>

            <svg
              className="path2"
              width="322"
              height="358"
              viewBox="0 0 322 358"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M311.5 8.5V50.5L189.5 13.5V190.5L74.5 202V283M74.5 283L10.5 288.5V346L196 328V273L74.5 283Z"
                style={{ stroke: "#6fe24e" }}
              />
            </svg>
            <svg
              className="path2"
              width="322"
              height="358"
              viewBox="0 0 322 358"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash-rev"
                d="M311.5 8.5V50.5L189.5 13.5V190.5L74.5 202V283M74.5 283L10.5 288.5V346L196 328V273L74.5 283Z"
                style={{ stroke: "#fff" }}
              />
            </svg>
            <div className="path2 box box2" style={{ borderColor: "#6fe24e" }}>
              Clean renewable
              <br />
              energy produced
            </div>

            <svg
              className="path3"
              width="56"
              height="244"
              viewBox="0 0 56 244"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M56 233.5C41.6667 233.5 12.7 226.3 11.5 197.5C10.3 168.7 11 54.1667 11.5 0.5"
                style={{ stroke: "#6fe24e" }}
              />
            </svg>
            <svg
              className="path3"
              width="56"
              height="244"
              viewBox="0 0 56 244"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash"
                d="M56 233.5C41.6667 233.5 12.7 226.3 11.5 197.5C10.3 168.7 11 54.1667 11.5 0.5"
                style={{ stroke: "#fff" }}
              />
            </svg>

            <svg
              className="path4"
              width="639"
              height="835"
              viewBox="0 0 639 835"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M10 0.5V48.5L314 138V453.5L346.5 463L534.5 444.5V596.5M600.5 751C592.125 750.601 567.7 760.2 568.5 793C569.22 822.5 594.26 827.58 605 824C612.5 821.5 626.824 809.759 628.5 793C630.5 773 621.5 752 600.5 751Z"
                style={{ stroke: "#ff00f0" }}
              />
            </svg>
            <svg
              className="path4"
              width="639"
              height="835"
              viewBox="0 0 639 835"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash-rev"
                d="M10 0.5V48.5L314 138V453.5L346.5 463L534.5 444.5V596.5M600.5 751C592.125 750.601 567.7 760.2 568.5 793C569.22 822.5 594.26 827.58 605 824C612.5 821.5 626.824 809.759 628.5 793C630.5 773 621.5 752 600.5 751Z"
                style={{ stroke: "#fff" }}
              />
            </svg>
            <div className="path4 box box4" style={{ borderColor: "#ff00f0" }}>
              Excess Clean
              <br />
              Energy to Grid
            </div>

            <svg
              className="path5"
              width="268"
              height="56"
              viewBox="0 0 268 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M1.5 45.5L266.5 10"
                style={{ stroke: "#de953c" }}
              />
            </svg>
            <svg
              className="path5"
              width="268"
              height="56"
              viewBox="0 0 268 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash"
                d="M1.5 45.5L266.5 10"
                style={{ stroke: "#f4ff98" }}
              />
            </svg>
            <div className="path5 box box5" style={{ borderColor: "#f4ff98" }}>
              Clean Stored Energy
              <br />
              Supplying Demand
            </div>

            <svg
              className="path6"
              width="1146"
              height="516"
              viewBox="0 0 1146 516"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M188 0V231L10 262V333.5L456.5 505L1136 384V333.5"
                style={{ stroke: "#248bff" }}
              />
            </svg>
            <svg
              className="path6"
              width="1146"
              height="516"
              viewBox="0 0 1146 516"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash"
                d="M188 0V231L10 262V333.5L456.5 505L1136 384V333.5"
                style={{ stroke: "#fff" }}
              />
            </svg>
            <div className="path6 box box6" style={{ borderColor: "#248bff" }}>
              Grid Energy Supplying
              <br />
              Real Time Demand
            </div>

            <svg
              className="path8"
              width="330"
              height="323"
              viewBox="0 0 330 323"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M4 313.5C14 310 33 296.5 33 273.5C33 251.409 32.1667 142.667 33 91M56.5 71.5L74.5 68L139 81M139 81V137.5L319.5 119V11L139 26V81Z"
                style={{ stroke: "#03eaff" }}
              />
            </svg>
            <svg
              className="path8"
              width="330"
              height="323"
              viewBox="0 0 330 323"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash"
                d="M4 313.5C14 310 33 296.5 33 273.5C33 251.409 32.1667 142.667 33 91M56.5 71.5L74.5 68L139 81M139 81V137.5L319.5 119V11L139 26V81Z"
                style={{ stroke: "#fff" }}
              />
            </svg>
            <div className="path8 box box8" style={{ borderColor: "#03eaff" }}>
              Extra Clean Energy
              <br />
              To Energy Storage
            </div>

            <svg
              className="path7"
              width="320"
              height="418"
              viewBox="0 0 320 418"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M10 0.5V295.5L316.5 408.5"
                style={{ stroke: "#ff1b00" }}
              />
            </svg>
            <svg
              className="path7"
              width="320"
              height="418"
              viewBox="0 0 320 418"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash"
                d="M10 0.5V295.5L316.5 408.5"
                style={{ stroke: "#fff" }}
              />
            </svg>
            <div className="path7 box box7" style={{ borderColor: "#ff1b00" }}>
              Energy In From Grid
            </div>

            <svg
              className="path9"
              width="320"
              height="418"
              viewBox="0 0 320 418"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="path"
                d="M10 0.5V295.5L316.5 408.5"
                style={{ stroke: "#ff1b00" }}
              />
            </svg>
            <svg
              className="path9"
              width="320"
              height="418"
              viewBox="0 0 320 418"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="dash-rev"
                d="M10 0.5V295.5L316.5 408.5"
                style={{ stroke: "#fff" }}
              />
            </svg>
            <div className="path9 box box9" style={{ borderColor: "#ff1b00" }}>
              Extra Clean Energy
              <br />
              Exported To Grid
            </div>
          </div>
          <img
            className="bgImg"
            id="bg-img"
            src="Mechanical-Room-Render_v05.jpg"
          />
        </div>
      </div>
    </div>
  );
}
